<template>
  <div class="sb-radio" :class="componentClasses">
    <label>
      <input
        :id="id"
        v-model="internalValue"
        v-bind="rootAttrs"
        class="sb-radio__input"
        type="radio"
        :name="name"
        :value="nativeValue"
        :required="required"
        :aria-disabled="disabled"
        :aria-label="hideLabel ? label : undefined"
        :aria-describedby="description ? `sb-radio-hint-${uid}` : undefined"
        @click="handleClick"
      />
      <span v-if="label && !hideLabel" class="sb-radio__label">
        {{ label }}
      </span>
    </label>
    <span
      v-if="description && !hideLabel"
      :id="`sb-radio-hint-${uid}`"
      class="sb-radio__description"
    >
      {{ description }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import { computed, watch, ref, useId, useAttrs } from 'vue'
import { reactiveOmit } from '@vueuse/core'

defineOptions({
  inheritAttrs: false,
})

/**
 * The props for the SbRadio component
 */
type RadioProps = {
  /**
   * The label of the radio
   */
  label?: string

  /**
   * To disable the radio. But, it will still be visible by the screen readers
   */
  disabled?: boolean

  /**
   * To hide the label as a text, but still binding the label to the radio
   */
  hideLabel?: boolean

  /**
   * The radio ID
   */
  id?: string

  /**
   * Set the value of the radio
   */
  modelValue?: string | number | boolean

  /**
   * Name of the radio
   */
  name?: string

  /**
   * The value of the radio
   */
  nativeValue?: string | number | boolean

  /**
   * To define the radio as required. Useful for form validation
   */
  required?: boolean

  /**
   * To define the radio as inline
   */
  inline?: boolean

  /**
   * Extends the radio with a description
   */
  description?: string
}

const props = withDefaults(defineProps<RadioProps>(), {
  disabled: false,
  hideLabel: false,
  required: false,
  inline: false,
})

const attrs = useAttrs()

const rootAttrs = reactiveOmit(attrs, ['class'])

const emit = defineEmits<{
  /**
   * update:modelValue event.
   *
   * @event update:modelValue
   * @property {string | number | boolean | undefined} nativeValue the updated modelValue of the radio
   */
  (e: 'update:modelValue', value: string | number | boolean | undefined): void
}>()

const internalValue = ref(props.modelValue)

const componentClasses = computed(() => [
  useAttrs().class,
  props.inline && 'sb-radio--inline',
])

const uid = useId()

watch(
  () => props.modelValue,
  (newValue) => {
    internalValue.value = newValue
  },
)

function handleClick(event: Event): void {
  if (props.disabled) {
    event.preventDefault()
    event.stopPropagation()
    return
  }

  /**
   * Emit the nativeValue if the radio button is not disabled
   *
   * @event update:modelValue
   * @property {Boolean, String, Number} modelValue the updated modelValue of the radio
   */
  emit('update:modelValue', props.nativeValue)
}
</script>
